import { Eye } from "lucide-react";
import ProxyImage from "@/components/ProxyImage.jsx";
import { Avatar } from "@/components/ui/Display.jsx";
import { PRODUCTIVITY, toneOf } from "@/lib/tokens.js";
import { cx } from "@/lib/cx.js";

/*
 * One thumbnail in the grid, from shotCard() in screenshots.js.
 *
 * The blur is a CSS filter over a full-resolution image, which is a privacy
 * feature only in the sense that it stops a passer-by reading the screen — the
 * unblurred pixels are still in the response. That is how the desktop uploads
 * it (`blurred` is a flag on the record, not a second rendering), so the label
 * says "Blurred for privacy" rather than promising redaction.
 */
export default function ShotCard({ shot, onOpen, onExpired }) {
    const p = PRODUCTIVITY[shot.productivityStatus] ?? PRODUCTIVITY.unrated;

    return (
        <button
            type="button"
            onClick={onOpen}
            data-shot={shot.key}
            title={shot.title || shot.application}
            className="group relative overflow-hidden rounded-lg border border-slate-200 bg-white text-left shadow-xs transition hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-md dark:border-slate-800 dark:bg-slate-900 dark:hover:border-brand-700"
        >
            <span className="relative block aspect-16/10 overflow-hidden bg-slate-100 dark:bg-slate-800">
                <ProxyImage
                    src={shot.src}
                    alt={shot.title || shot.application}
                    onExpired={onExpired}
                    className="size-full"
                    imgClassName={cx(shot.blurred && "shot-blurred")}
                />

                <span className="absolute top-1.5 left-1.5 rounded bg-slate-900/75 px-1.5 py-0.5 font-mono text-[10.5px] font-medium text-white">
                    {shot.time}
                </span>

                <span
                    title={p.label}
                    className={cx(
                        "absolute top-2 right-2 size-2.5 rounded-full ring-2 ring-white/80",
                        toneOf(p.tone).dot,
                    )}
                />

                {/* Only when the capture really covered more than one monitor —
                    otherwise every card would carry a meaningless "1 of 1". */}
                {shot.displayCount > 1 && (
                    <span className="absolute bottom-1.5 right-1.5 rounded bg-slate-900/75 px-1.5 py-0.5 font-mono text-[10px] font-medium text-white">
                        {shot.display}/{shot.displayCount}
                    </span>
                )}

                {shot.blurred && (
                    <span className="absolute inset-x-0 bottom-0 bg-amber-500/90 py-0.5 text-center text-[10px] font-semibold text-white">
                        Blurred for privacy
                    </span>
                )}

                <span className="absolute inset-0 flex items-center justify-center bg-slate-950/55 opacity-0 transition group-hover:opacity-100">
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-white px-2.5 py-1.5 text-[12.5px] font-semibold text-slate-900 shadow">
                        <Eye className="size-4" strokeWidth={2} aria-hidden="true" />
                        View
                    </span>
                </span>
            </span>

            <span className="block min-w-0 p-2.5">
                <span className="block truncate text-[12.5px] font-medium">{shot.application}</span>
                <span className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-slate-400 dark:text-slate-500">
                    <Avatar name={shot.user.name} size="xs" />
                    <span className="truncate">{shot.user.name}</span>
                </span>
            </span>
        </button>
    );
}
